import axios from 'axios';
import Consts from '@/constants/const';
import { DEVICE_ID_V2, SIGNATURE_TOKEN } from '@weee-fe/core';
import { NewAxiosRequestConfig } from './axios';
import { getPageEventNameV2 } from '@/tracks/tracker';

/**
 * 创建专门用于埋点的axios实例
 * 避免与主axios实例的拦截器产生循环调用
 */
const createTrackingAxios = () => {
  const trackingInstance = axios.create({
    withCredentials: false,
    timeout: 5000, // 埋点请求使用较短的超时时间
    baseURL: process.env.API_HOST || process.env.NEXT_PUBLIC_API_HOST,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      Platform: 'h5'
    }
  });

  // 只添加简单的错误日志，不添加业务拦截器
  trackingInstance.interceptors.response.use(
    response => response,
    error => {
      console.error('[Tracking API Error]:', error.message);
      return Promise.reject(error);
    }
  );

  return trackingInstance;
};

// 埋点专用axios实例
const trackingAxios = createTrackingAxios();

/**
 * 生成唯一ID
 * 使用时间戳 + 随机数的方式生成类似UUID的唯一标识符
 */
const generateId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomPart1 = Math.random().toString(36).substring(2, 10);
  const randomPart2 = Math.random().toString(36).substring(2, 10);
  return `${timestamp}-${randomPart1}-${randomPart2}`;
};

/**
 * 安全获取header值的辅助函数
 */
const getHeaderSafely = (headers: any, key: string, defaultValue: string = ''): string => {
  return headers?.[key] || defaultValue;
};

/**
 * 构建服务端埋点数据
 */
const buildServerTrackingData = (errorInfo: { message_id: string; message: string }, config: NewAxiosRequestConfig) => {
  const now = Math.floor(Date.now() / 1000);
  const requestParams = config?.params || config?.data || {};
  const lang = getHeaderSafely(config.headers, Consts.LANG, 'en');

  // 🛡️ 安全处理页面URL，避免传入undefined导致getPageEventNameV2报错
  const resolvedUrl = config?.pageInfo?.resolvedUrl || '/';
  const pageKey = getPageEventNameV2(resolvedUrl);

  return {
    app_version: '1',
    device_id: getHeaderSafely(config.headers, Consts.DEVICE_ID),
    os_language: '',
    os_version: '',
    platform: 'MWeb',
    screen_height: '',
    screen_width: '',
    sdk_version: '2.0.0',
    user_agent: getHeaderSafely(config.headers, 'User-Agent', 'Server-Side-Tracker'),
    d2: getHeaderSafely(config.headers, DEVICE_ID_V2),
    st: getHeaderSafely(config.headers, SIGNATURE_TOKEN),
    event_items: [
      {
        referer_page_key: '',
        referer_page_url: '',
        referer_view_id: '',
        page_key: pageKey,
        page_url: `${process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST}/${lang}${
          config?.pageInfo?.resolvedUrl || ''
        }`,
        view_id: '',
        session_id: getHeaderSafely(config.headers, Consts.WEEE_HEADER_SESSION_TOKEN),
        language: lang,
        zipcode: getHeaderSafely(config.headers, Consts.WEEE_ZIPCODE),
        time: now,
        id: generateId(),
        type: 't2_status_metrics',
        params: {
          co: {
            name: 'business_exception',
            value: errorInfo.message_id,
            target: `${config?.method} ${config?.baseURL || ''}${config?.url || ''}`,
            result: 'success',
            extra: {
              message: errorInfo.message,
              params: JSON.stringify(requestParams),
              isHaveToken: !!getHeaderSafely(config.headers, Consts.AUTH_TOKEN_IN_HEADERS)?.replace('Bearer ', ''),
              zipcode: getHeaderSafely(config.headers, Consts.WEEE_ZIPCODE),
              deliveryDate: getHeaderSafely(config.headers, Consts.WEEE_DATE)
            }
          }
        }
      }
    ],
    message_id: generateId()
  };
};

/**
 * 服务端API错误埋点
 * @param errorInfo 错误信息
 * @param config 请求配置
 */
export const trackServerApiError = async (
  errorInfo: { message_id: string; message: string },
  config: NewAxiosRequestConfig
): Promise<void> => {
  try {
    // 构建埋点数据
    const trackingData = buildServerTrackingData(errorInfo, config);

    // 异步调用埋点接口（不阻塞主流程）
    trackingAxios.post(process.env.NEXT_PUBLIC_TRACKER_API, trackingData).catch(error => {
      // 埋点失败不影响主流程，只记录日志
      console.error('[Server Tracking Error]:', error.message);
    });

    // 记录到控制台，便于调试
    console.log(`[Server API Error] ${config.url} - ${errorInfo.message_id}: ${errorInfo.message}`);
  } catch (error) {
    // 埋点处理异常不应影响主流程
    console.error('[Server Tracking Exception]:', error.message);
  }
};

/**
 * 检查是否需要进行服务端埋点
 * @param messageId API响应的message_id
 * @returns 是否需要埋点
 */
export const shouldTrackServerError = (messageId: string): boolean => {
  return messageId !== '10000' && messageId !== '90500';
};
